import {
  useCreateBuildingPackageMutation,
  useCreateBuildingPackageBuildingJsonMutation,
  useCreateLayoutOptimizationMutation,
  useCreatePackageMutation,
  useStartPipelineMutation,
  useUpdatePackageMutation,
  useProjectId,
  useBlobUpload,
  useFileUpload,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ServiceEntityType, ServiceEntityTypeLabels } from '@consigli/types';
import { useState } from 'react';
import { HiOutlinePlusSm } from 'react-icons/hi';

import { Field, SheetForm, Select } from '@/components';

import { FormSubmissionParams, handleFormSubmit } from './logic/form-submission';
import { ServiceFormRenderer } from './logic/service-form-renderer';
import { useUnifiedServiceForm } from './logic/use-unified-service-form';
import { validateServiceForm } from './validator';

/**
 * Add Service Form for creating a new service instance on a project.
 * It uses a unified form state management and renders the appropriate form based on the service type.
 * It combines building package, document package and layout optimization forms into a single form.
 * This Abstracts away the differences between the service types for the user.
 */
export const AddServiceForm = () => {
  const serviceTypes = Object.values(ServiceEntityType);
  const [createPackage] = useCreatePackageMutation();
  const [updatePackage] = useUpdatePackageMutation();
  const [createBuildingPackage] = useCreateBuildingPackageMutation();
  const [createBuildingPackageBuildingJson] = useCreateBuildingPackageBuildingJsonMutation();
  const [createLayoutOptimization] = useCreateLayoutOptimizationMutation();
  const [startPipeline] = useStartPipelineMutation();
  const projectId = useProjectId();
  const { upload: uploadBlob } = useBlobUpload();
  const { upload: uploadFile } = useFileUpload();

  // Add submission state management
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use unified form state management
  const {
    serviceType,
    name,
    documentPackageType,
    buildingPackageType,
    layoutOptimizationType,
    language,
    inputType,
    comments,
    floorHeight,
    files,
    selectedConflictTypes,
    employerRole,
    rfpRegion,
    // Site Due Diligence fields
    siteDueDiligenceFacilityType,
    // templateMode,
    // customTemplateFile,
    // selectedCustomer,
    locationLat,
    locationLon,
    documentFiles,
    // gisFiles,
    // urls,
    // allowLLMForDocuments,
    // Bid Review (OFFER) fields
    bidReviewBids,
    bidReviewServices,
    bidReviewAutoFillLevelingSheet,
    bidReviewLevelingSheetFiles,
    tenderPackageMode,
    existingTenderPackageId,
    tenderPackageName,
    tenderFiles,
    // Version Compare fields
    versionComparePrimaryMode,
    versionCompareSecondaryMode,
    versionComparePrimaryPackageId,
    versionCompareSecondaryPackageId,
    versionComparePrimaryFiles,
    versionCompareSecondaryFiles,
    versionCompareCommentsMode,
    versionCompareCommentRegisterFiles,
    errors,
    updateField,
    resetForm,
    setErrorsFromValidation,
    clearErrors,
  } = useUnifiedServiceForm();

  /**
   * Wrapper for the form submission.
   * It prevents duplicate submissions and sets the submission state.
   * @returns The result of the form submission.
   */
  const handleFormSubmitWrapper = async (): Promise<boolean> => {
    setIsSubmitting(true);

    try {
      // Validate form data before submission
      const formData = {
        serviceType,
        name,
        packageType:
          serviceType === ServiceEntityType.DOCUMENT_PACKAGE
            ? documentPackageType
            : serviceType === ServiceEntityType.BUILDING_PACKAGE
              ? buildingPackageType
              : layoutOptimizationType,
        language,
        inputType,
        comments,
        floorHeight,
        files,
      };

      const validation = validateServiceForm(formData);
      if (!validation.isValid) {
        setErrorsFromValidation(validation.errors);
        return false;
      }

      const params: FormSubmissionParams = {
        serviceType,
        name,
        documentPackageType,
        buildingPackageType,
        layoutOptimizationType,
        language,
        inputType,
        comments,
        floorHeight,
        files,
        selectedConflictTypes,
        employerRole,
        rfpRegion,
        // Site Due Diligence fields
        siteDueDiligenceFacilityType,
        // templateMode,
        // customTemplateFile,
        // selectedCustomer,
        locationLat,
        locationLon,
        documentFiles,
        // gisFiles,
        // urls,
        // allowLLMForDocuments,
        // Bid Review (OFFER) fields
        bidReviewBids,
        bidReviewServices,
        bidReviewAutoFillLevelingSheet,
        bidReviewLevelingSheetFiles,
        tenderPackageMode,
        existingTenderPackageId,
        tenderPackageName,
        tenderFiles,
        // Version Compare fields
        versionComparePrimaryMode,
        versionCompareSecondaryMode,
        versionComparePrimaryPackageId,
        versionCompareSecondaryPackageId,
        versionComparePrimaryFiles,
        versionCompareSecondaryFiles,
        versionCompareCommentsMode,
        versionCompareCommentRegisterFiles,
        projectId,
        resetForm,
        createPackage,
        updatePackage,
        createBuildingPackage,
        createBuildingPackageBuildingJson,
        createLayoutOptimization,
        startPipeline,
        uploadBlob,
        uploadFile,
      };

      const result = await handleFormSubmit(params);

      if (!result.isValid) {
        return false;
      }

      clearErrors();
      return result.isValid;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SheetForm
      title="Add New Service"
      description="Fill in the details to add a new service instance"
      trigger={
        <Button>
          Add Service <HiOutlinePlusSm />
        </Button>
      }
      onSubmit={handleFormSubmitWrapper}
      isSubmitting={isSubmitting}
      className="w-[600px] max-w-[800px] px-2 py-5 sm:!max-w-[800px]"
    >
      <Field
        label="Select Service Type"
        htmlFor="service-category-select"
        required
        className="mb-4"
        error={errors.serviceType}
      >
        <Select
          options={serviceTypes}
          value={serviceType || undefined}
          onValueChange={(value) => updateField('serviceType', value)}
          getOptionLabel={(option) => ServiceEntityTypeLabels[option]}
          getOptionValue={(option) => option}
          placeholder="Select Service Type"
          className="w-full"
        />
      </Field>

      <ServiceFormRenderer
        serviceType={serviceType}
        projectId={projectId}
        state={{
          serviceType,
          name,
          language,
          files,
          documentPackageType,
          selectedConflictTypes,
          employerRole,
          rfpRegion,
          buildingPackageType,
          inputType,
          layoutOptimizationType,
          comments,
          floorHeight,
          // Site Due Diligence fields
          siteDueDiligenceFacilityType,
          // templateMode,
          // customTemplateFile,
          // selectedCustomer,
          locationLat,
          locationLon,
          documentFiles,
          // gisFiles,
          // urls,
          // allowLLMForDocuments,
          // Bid Review (OFFER) fields
          bidReviewBids,
          bidReviewServices,
          bidReviewAutoFillLevelingSheet,
          bidReviewLevelingSheetFiles,
          tenderPackageMode,
          existingTenderPackageId,
          tenderPackageName,
          tenderFiles,
          // Version Compare fields
          versionComparePrimaryMode,
          versionCompareSecondaryMode,
          versionComparePrimaryPackageId,
          versionCompareSecondaryPackageId,
          versionComparePrimaryFiles,
          versionCompareSecondaryFiles,
          versionCompareCommentsMode,
          versionCompareCommentRegisterFiles,
        }}
        errors={errors}
        onFieldChange={updateField}
      />
    </SheetForm>
  );
};
